import { noteRefreshSucceeded, reportNoteRefreshFailure } from 'utils/noteRefreshErrors'
import { log } from 'utils/logger'
import { matchesLoadNoteKey } from '@numeo/types-core'
import { IUpdaterLoadNotesUpdateMessage } from '@numeo/types'
import { ongoingLoadsApi } from '@/pages/dashboard/pages/updater-agent/pages/ongoing-loads/api/ongoing-loads.api'
import type { AppDispatch, RootState } from 'store'
import { loadNotesReceived, selectLatestLoadNotesById } from 'store/loadNotes'

interface Recovery {
    loadId: string
    projectName: string | undefined
    targetRevision: number
    attempts: number
    forceRead?: boolean
    inFlight?: boolean
    timer?: ReturnType<typeof setTimeout>
    controller: AbortController
    dispatch: AppDispatch
    getState: () => RootState
}

// Only missing revisions on loaded rows enter this transient queue. Entries are removed on completion;
// each request has a timeout and at most one retry. Coalesce bursts and spread gap reads across tabs.
const recoveries = new Map<string, Recovery>()
let activeReads = 0
const MAX_ACTIVE_READS = 2
const projectName = (state: RootState) => state.auth?.application?.projectName
const currentLoad = (entry: Recovery) => selectLatestLoadNotesById(entry.getState()).get(entry.loadId)
const schedule = (key: string, entry: Recovery): void => {
    entry.timer = setTimeout(() => void recover(key, entry), 250 + Math.floor(Math.random() * 1000))
}

const recoverReconnectBatch = async (first: Recovery): Promise<void> => {
    const batch: [string, Recovery][] = []
    for (const [key, entry] of recoveries) {
        if (!entry.forceRead || entry.inFlight || entry.projectName !== first.projectName) continue
        if (!currentLoad(entry) || projectName(entry.getState()) !== entry.projectName) {
            if (entry.timer) clearTimeout(entry.timer)
            recoveries.delete(key)
            continue
        }
        if (entry.timer) clearTimeout(entry.timer)
        entry.timer = undefined
        entry.inFlight = true
        entry.attempts++
        entry.controller = first.controller
        batch.push([key, entry])
        if (batch.length === 100) break
    }
    if (!batch.length) return
    activeReads++
    const isCurrent = (key: string, entry: Recovery) => recoveries.get(key) === entry && projectName(entry.getState()) === entry.projectName
    const retry = (key: string, entry: Recovery, error: unknown) => {
        if (!isCurrent(key, entry)) return
        if (entry.attempts < 2) schedule(key, entry)
        else {
            recoveries.delete(key)
            reportNoteRefreshFailure(error, 'load_notes_revision_recovery')
        }
    }
    try {
        const snapshots = await ongoingLoadsApi.getLoadNotesBatch(
            batch.map(([, entry]) => entry.loadId),
            first.controller.signal
        )
        if (!batch.some(([key, entry]) => isCurrent(key, entry))) return
        const byId = new Map(snapshots.map((snapshot) => [snapshot.id, snapshot]))
        let behind = false
        for (const [key, entry] of batch) {
            if (!isCurrent(key, entry)) continue
            const snapshot = byId.get(entry.loadId)
            if (snapshot) {
                entry.dispatch(loadNotesReceived(snapshot))
                if ((currentLoad(entry)?.notesRevision ?? 0) < entry.targetRevision) {
                    behind = true
                    retry(key, entry, new Error('Load notes snapshot is behind the socket revision'))
                    continue
                }
            }
            // A missing snapshot means the load was removed; do not retry it or erase newer local notes.
            recoveries.delete(key)
        }
        if (!behind) noteRefreshSucceeded()
    } catch (error) {
        if (batch.some(([key, entry]) => isCurrent(key, entry))) {
            log.e('Load notes reconnect recovery failed', error, { count: batch.length })
            for (const [key, entry] of batch) retry(key, entry, error)
        }
    } finally {
        activeReads--
        for (const [key, entry] of batch) {
            entry.inFlight = false
            if (!isCurrent(key, entry) && recoveries.get(key) === entry) recoveries.delete(key)
        }
    }
}

const recover = async (key: string, entry: Recovery): Promise<void> => {
    entry.timer = undefined
    const isCurrent = () => recoveries.get(key) === entry && projectName(entry.getState()) === entry.projectName
    const load = currentLoad(entry)
    if (!isCurrent() || !load || (!entry.forceRead && (load.notesRevision ?? 0) >= entry.targetRevision)) {
        recoveries.delete(key)
        return
    }
    if (activeReads >= MAX_ACTIVE_READS) {
        schedule(key, entry)
        return
    }
    if (entry.forceRead) {
        await recoverReconnectBatch(entry)
        return
    }
    activeReads++
    entry.attempts++
    try {
        const snapshot = await ongoingLoadsApi.getLoadNotes(entry.loadId, entry.controller.signal)
        if (!isCurrent()) return
        entry.dispatch(loadNotesReceived(snapshot))
        if ((currentLoad(entry)?.notesRevision ?? 0) < entry.targetRevision) throw new Error('Load notes snapshot is behind the socket revision')
        noteRefreshSucceeded()
        recoveries.delete(key)
    } catch (error) {
        if (!isCurrent()) return
        log.e('Load notes revision recovery failed', error, { loadId: entry.loadId, attempt: entry.attempts })
        if (entry.attempts < 2) schedule(key, entry)
        else {
            recoveries.delete(key)
            reportNoteRefreshFailure(error, 'load_notes_revision_recovery')
        }
    } finally {
        activeReads--
        // A tenant switch may finish an older request after a new tenant has queued the same load id.
        if (!isCurrent() && recoveries.get(key) === entry) recoveries.delete(key)
    }
}

export const resetLoadNotesSync = (): void => {
    noteRefreshSucceeded()
    for (const entry of recoveries.values()) {
        if (entry.timer) clearTimeout(entry.timer)
        entry.controller.abort()
    }
    recoveries.clear()
}

/** Recover loaded rows even when no later socket delta reveals the disconnected edit. */
export const refreshLoadNotesAfterReconnect = (dispatch: AppDispatch, getState: () => RootState): void => {
    resetLoadNotesSync()
    const state = getState()
    for (const load of selectLatestLoadNotesById(state).values()) {
        const key = JSON.stringify([projectName(state), load.id])
        const entry: Recovery = {
            loadId: load.id,
            projectName: projectName(state),
            targetRevision: load.notesRevision ?? 0,
            forceRead: true,
            attempts: 0,
            controller: new AbortController(),
            dispatch,
            getState,
        }
        recoveries.set(key, entry)
        schedule(key, entry)
    }
}

export const handleLoadNotesUpdate = (message: IUpdaterLoadNotesUpdateMessage, dispatch: AppDispatch, getState: () => RootState): void => {
    const state = getState()
    const current = selectLatestLoadNotesById(state).get(message.loadId)
    if (!current || message.notesRevision <= (current.notesRevision ?? 0)) return
    if (message.notesRevision === (current.notesRevision ?? 0) + 1) {
        const notes = [...(current.notes ?? [])]
        const index = notes.findIndex((note) => matchesLoadNoteKey(note, message.noteKey))
        if (message.note) {
            if (index < 0) notes.push(message.note)
            else notes[index] = message.note
        } else if (index >= 0) notes.splice(index, 1)
        dispatch(loadNotesReceived({ id: message.loadId, notes, notesRevision: message.notesRevision }))
        return
    }
    const key = JSON.stringify([projectName(state), message.loadId])
    const existing = recoveries.get(key)
    if (existing) existing.targetRevision = Math.max(existing.targetRevision, message.notesRevision)
    else {
        const entry: Recovery = {
            loadId: message.loadId,
            projectName: projectName(state),
            targetRevision: message.notesRevision,
            attempts: 0,
            controller: new AbortController(),
            dispatch,
            getState,
        }
        recoveries.set(key, entry)
        schedule(key, entry)
    }
}
